import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import pick from 'lodash/pick';

import cookieStorage from 'helpers/cookieStorage';

import { apolloClient } from 'hooks/withGraphQL';

import { AUTH_HEADERS as AUTH_HEADERS_COOKIE, IS_VERIFIED } from 'store/consts';

import api from 'api';

import { resetPlayerThunk } from './player';
import { resetSignUp } from './signUp';
import { clearAlreadyListenedStories } from './userAlreadyListenedStories';

export interface AuthStateHeaders {
  'access-token': string;
  uid: string;
  client: string;
  expiry?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  accessToken?: string;
  headers?: AuthStateHeaders;
  rememberMe?: boolean;
  isVerified: boolean;
  error: any;
}

export const initialAuthState: AuthState = {
  isAuthenticated: false,
  headers: undefined,
  isVerified: false,
  error: null,
};

interface AuthHeadersPayload {
  headers?: AuthStateHeaders & {
    active_subscription: string;
  };
  rememberMe?: boolean;
}

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    authLogout: () => ({ ...initialAuthState }),
    authHeaders: (state, action: PayloadAction<AuthHeadersPayload>) => {
      state.accessToken = action.payload?.headers?.['access-token'];
      state.headers = action.payload?.headers
        ? {
            ...state.headers,
            ...action.payload.headers,
          }
        : undefined;
    },
    authSuccess: (state) => {
      state.isAuthenticated = true;
    },
    enableRememberMe: (state) => {
      state.rememberMe = true;
    },
  },
});

export default authSlice.reducer;

export const { authHeaders, enableRememberMe, authLogout, authSuccess } =
  authSlice.actions;

interface IUpdateHeaders {
  headers?: {
    'access-token': string;
    client: string;
    uid: string;
    pin_confirmed: string;
    active_subscription: string;
    expiry: string;
  };
}

export function updateHeaders({ headers }: IUpdateHeaders = {}, rememberMe?) {
  return (dispatch) => {
    if (headers && headers['access-token']) {
      dispatch(
        authSlice.actions.authHeaders({
          headers: {
            ...pick(headers, [
              'access-token',
              'client',
              'uid',
              'pin_confirmed',
              'active_subscription',
              'expiry',
            ]),
          },
          rememberMe,
        }),
      );
    }
  };
}

// SIDE EFFECTS
export function logout(options = { invalidateToken: true }) {
  return (dispatch, getState) => {
    if (options.invalidateToken && getState().auth.headers['access-token']) {
      api.auth.invalidateToken();
    }

    cookieStorage.removeItem(IS_VERIFIED);
    cookieStorage.removeItem(AUTH_HEADERS_COOKIE);
    dispatch(resetPlayerThunk());
    dispatch(authLogout());
    dispatch(resetSignUp());
    dispatch(clearAlreadyListenedStories());
    apolloClient.cache.reset();
  };
}
